import { useEffect, useState, useCallback } from 'react'
import { router } from '@inertiajs/react'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '@/components/ui/sheet'
import { Skeleton } from '@/components/ui/skeleton'
import TasksShow from '../Show'
import { EditTaskSheetContent } from './EditTaskSheetContent'
import { CreateTaskSheetContent } from './CreateTaskSheetContent'
import type { TaskDetail, TaskRow, TaskComment, AssigneeOption } from '@/types/work'

// Sheet wrapper around the existing Tasks/Show page. Mirrors the
// OpportunityDetailSheet pattern: keyed by taskId, fetches the JSON
// branch of TasksController#show, and reuses the Show component as the
// sheet body via its `embedded` mode.
//
// Sheet state is component-local in the parent (Tasks/Index): when
// taskId is non-null the sheet opens and fetches; null closes it. The
// standalone /work/tasks/:id route still renders Show as a full page,
// so direct deep-links from emails / notifications keep working.

interface ShowPayload {
  task: TaskDetail
  subtasks: TaskRow[]
  comments: TaskComment[]
  assignee_options: AssigneeOption[]
}

interface TaskDetailSheetProps {
  taskId: number | null
  onOpenChange: (open: boolean) => void
  // Replaces the current sheet content with the clicked subtask. The
  // parent owns selectedTaskId so it just calls setSelectedTaskId(id).
  onSelectTask: (id: number) => void
}

export function TaskDetailSheet({ taskId, onOpenChange, onSelectTask }: TaskDetailSheetProps) {
  const [data, setData] = useState<ShowPayload | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  // 'detail' shows the read-only Show component;
  // 'edit' swaps in the edit form for the same task;
  // 'create_subtask' swaps in the create form pre-set to make the
  // current task the parent. All three reset to 'detail' when the
  // sheet closes (taskId becomes null).
  const [mode, setMode] = useState<'detail' | 'edit' | 'create_subtask'>('detail')

  const fetchDetail = useCallback(
    (id: number, signal?: AbortSignal) => {
      setLoading(true)
      setError(false)
      return fetch(`/work/tasks/${id}`, {
        headers: { Accept: 'application/json' },
        signal,
      })
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`)
          return res.json() as Promise<ShowPayload>
        })
        .then((json) => setData(json))
        .catch((err) => {
          if (err.name === 'AbortError') return
          console.error('TaskDetailSheet fetch failed:', err)
          setError(true)
        })
        .finally(() => setLoading(false))
    },
    [],
  )

  useEffect(() => {
    if (!taskId) {
      setData(null)
      setError(false)
      setMode('detail')
      return
    }
    setMode('detail')
    const controller = new AbortController()
    fetchDetail(taskId, controller.signal)
    return () => controller.abort()
  }, [taskId, fetchDetail])

  // Re-fetch the current task without changing taskId AND ask Inertia
  // to refresh the index list behind the sheet so a status / priority
  // change inside the sheet is reflected on the row immediately.
  // Used by CommentThread / InlineStatusEdit success callbacks.
  const reload = useCallback(() => {
    if (taskId) fetchDetail(taskId)
    router.reload({ only: [ 'tasks', 'pagy', 'filters' ] })
  }, [taskId, fetchDetail])

  return (
    <Sheet open={!!taskId} onOpenChange={onOpenChange}>
      <SheetContent
        className="w-[700px] sm:max-w-[700px] overflow-y-auto p-0"
        aria-describedby={undefined}
      >
        <VisuallyHidden.Root>
          <SheetTitle>Détail de la tâche</SheetTitle>
        </VisuallyHidden.Root>

        {loading && !data ? (
          <div className="space-y-4 p-6">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : error ? (
          <div className="p-6">
            <p className="text-sm text-destructive">
              Impossible de charger la tâche. Réessayez plus tard.
            </p>
          </div>
        ) : data ? (
          <div className="p-6">
            {mode === 'edit' && taskId ? (
              <EditTaskSheetContent
                taskId={taskId}
                onSaved={() => {
                  setMode('detail')
                  reload()
                }}
                onCancel={() => setMode('detail')}
              />
            ) : mode === 'create_subtask' && taskId ? (
              <CreateTaskSheetContent
                parentTaskId={taskId}
                parentTaskTitle={data.task.title}
                onCreated={() => {
                  // Return to the parent task's detail; the reload will
                  // pull in the new subtask row.
                  setMode('detail')
                  reload()
                }}
                onCancel={() => setMode('detail')}
              />
            ) : (
              <TasksShow
                task={data.task}
                subtasks={data.subtasks}
                comments={data.comments}
                embedded
                assigneeOptions={data.assignee_options}
                onSelectTask={onSelectTask}
                onEnterEdit={() => setMode('edit')}
                onCreateSubtask={() => setMode('create_subtask')}
                onAfterMutation={reload}
              />
            )}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  )
}
